import { Injectable, inject, signal } from '@angular/core'
import { ActivatedRoute, ActivatedRouteSnapshot, NavigationEnd, Router, RoutesRecognized } from '@angular/router'
import { RouteConfigInterface, TenantRouteConfigInterface } from '@ui-core/interfaces/route-config.interface'
import { filter, map, pairwise, startWith, tap } from 'rxjs'
import { RouteService } from './route.service'

@Injectable({
    providedIn: 'root'
})
export class RouterStateService {
    private router = inject(Router)
    private route = inject(ActivatedRoute)
    private routeService = inject(RouteService)

    private routeHistory: Record<string, string> = {}
    isRouteLoaded = signal(false)

    subscribeRouterStateChanges(): void {
        this.router.events
            .pipe(
                filter((event) => event instanceof RoutesRecognized),
                map((event) => event as RoutesRecognized),
                pairwise(),
                tap((val: RoutesRecognized[]) => {
                    let previousUrl = val[0].urlAfterRedirects
                    const currentUrl = val[1].urlAfterRedirects
                    let parentUrl = ''

                    const prevUrlLen = previousUrl.split('?')[0].split('/')
                    const currentUrlLen = currentUrl.split('?')[0].split('/')

                    const intersection = prevUrlLen.filter((value) => currentUrlLen.includes(value))
                    const urlDiff = intersection.slice(1)

                    if (urlDiff.length === 0) {
                        this.routeHistory = {}
                        previousUrl = ''
                    }

                    if (urlDiff.length > 0) {
                        if (currentUrlLen.length > prevUrlLen.length) {
                            parentUrl = previousUrl
                        }
                    }

                    if (parentUrl) {
                        const moduleParent = parentUrl.split('?')[0]
                        this.routeHistory[moduleParent] = parentUrl
                    }

                    this.routeService.setCurrentRoute(currentUrl)
                    this.routeService.setParentRoute(parentUrl)
                    this.routeService.setPreviousRoute(previousUrl)
                })
            )
            .subscribe()

        this.router.events
            .pipe(
                filter((event): event is NavigationEnd => event instanceof NavigationEnd),
                startWith(null),
                map(() => this.routeFirstChild(this.route.snapshot)),
                map((activeRoute) => ({
                    route: activeRoute,
                    staticPath:
                        activeRoute.routeConfig?.path
                            ?.split('/')
                            .filter((segment) => !segment.startsWith(':'))
                            .join('/') ?? ''
                }))
            )
            .subscribe(({ route, staticPath }) => {
                if (staticPath !== '') {
                    this.routeService.setData(route.data)
                    this.routeService.setCurrentRoute(staticPath)
                    this.isRouteLoaded.set(true)
                }
            })
    }

    getIsRouteLoaded(): boolean {
        return this.isRouteLoaded()
    }

    routeFirstChild(route: ActivatedRouteSnapshot): ActivatedRouteSnapshot {
        while (route.firstChild) {
            route = route.firstChild
        }

        return route
    }

    overrideRouterData(routeConfig: TenantRouteConfigInterface): void {
        routeConfig.routes.forEach((route: RouteConfigInterface) => {
            const index = this.router.config.findIndex((r) => r.path === route.path)

            const routeFound = this.router.config[index]
            if (routeFound) {
                const routeData = routeFound.data || {}

                if (!routeData) {
                    this.router.config[index].data = {}
                }

                this.router.config[index].data = {
                    ...routeData,
                    permission: route.permission
                }
            }
        })
    }
}
